/**
 * Directed origin→destination pair, keyed on the rename-stable cluster
 * `slug` — never a station `id`, which identifies a station×line record.
 * One string serves as the storage key, the deep-link payload and the
 * analytics `route_key`, so the three can never disagree.
 *
 * The shape is the LOCKED cross-platform contract (`2026-08-06-task-114-115-
 * route-library-discovery-design.md` §2.1): iOS `RouteKey` and the Android
 * `:metro-core:route-library` type must agree byte-for-byte on the canonical
 * id, on the directedness (`A→B ≠ B→A`), and on rejecting `A→A`.
 */
export interface RouteKey {
  readonly fromSlug: string
  readonly toSlug: string
}

/**
 * Contract separator — three `>`. Slugs are kebab-case, so `>` cannot
 * collide with slug content and the id stays unambiguously splittable.
 */
const SEPARATOR = '>>>'

/**
 * Build a key, or `null` when the pair is not a route.
 *
 * Rejects a blank slug (nothing to key on) and a same-station pair: `A→A`
 * is not a route, and an implementation written from an earlier draft of
 * the contract accepted `A>>>A` and diverged from iOS — §2.1 names that
 * failure by name.
 */
export function makeRouteKey(
  fromSlug: string | null | undefined,
  toSlug: string | null | undefined,
): RouteKey | null {
  const from = fromSlug?.trim() ?? ''
  const to = toSlug?.trim() ?? ''
  if (from === '' || to === '') return null
  if (from === to) return null
  return { fromSlug: from, toSlug: to }
}

/** The `"<fromSlug>>>><toSlug>"` string every platform stores and reports. */
export function canonicalId(key: RouteKey): string {
  return `${key.fromSlug}${SEPARATOR}${key.toSlug}`
}

/**
 * Inverse of {@link canonicalId}. Returns `null` for anything that is not
 * exactly two non-blank, distinct halves — a stored row whose id no longer
 * parses is dropped rather than trusted.
 */
export function parseCanonicalId(id: string): RouteKey | null {
  const parts = id.split(SEPARATOR)
  if (parts.length !== 2) return null
  return makeRouteKey(parts[0], parts[1])
}
